import type { JSONContent } from '@tiptap/core'
import type { News, Translatable, TranslatedNews, UserRole } from './types'
import { UserRoleData, Department } from './types'

export function isObjectId(value?: string): boolean {
  return !!value && !!value.match(/^[0-9a-fA-F]{24}$/)
}

export function isUUID(value?: string): boolean {
  return !!value && !!value.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i)
}

export function isMinecraftUsername(value?: string): boolean {
  return !!value && !!value.match(/^[a-zA-Z0-9_]{2,16}$/i)
}

export function isLegacyUsername(value?: string): boolean {
  return !!value && !!value.match(/^[0-9]+-.{0,16}$/i)
}

export const TEAM_DEPARTMENTS = [
  Department.MANAGEMENT,
  Department.SUPPORT,
  Department.PROMOTION,
  Department.DEVELOPMENT,
  Department.CREATIVE,
]

export function isTeamRole(role: UserRole) {
  const department = UserRoleData[role].department
  return TEAM_DEPARTMENTS.includes(department)
}

export function deepClone<T>(value: T): T {
  return structuredClone(toRaw(value))
}

export function translate<T>(trans: Translatable<T> | null | undefined, locale: string | null): T | null {
  if (!trans || !locale) {
    return null
  }
  return trans[locale] ?? trans['en'] ?? Object.entries(trans)[0]?.[1] ?? null
}

export function translateNews<T>(news: News<T> | null | undefined, locale: string): TranslatedNews<T> | null {
  if (!news) {
    return null
  }
  const trans = translate(news.locales, locale) ?? {
    image: null,
    title: null,
    teaser: null,
    body: null,
  }
  return {
    ...news,
    ...trans,
    locales: Object.keys(news.locales),
  }
}

// marks supported by teaserExtensions()
const TEASER_MARKS = ['bold', 'italic', 'strike']

// uses first paragraph of content as teaser (compatible with teaserExtensions())
export function generateTeaser(content: JSONContent, length = 500): JSONContent | undefined {
  const para = content.content?.find(node => node.type === 'paragraph')
  if (!para || !para.content) {
    console.warn('could not generate teaser from content', content)
    return undefined
  }
  let characterCount = 0
  const texts = para.content
    .filter(node => node.type === 'text')
    .filter((text) => {
      characterCount += (text.text as string).length
      return characterCount < length
    })
    // copy to not mutate content and drop marks unknown to the teaser schema
    .map(text => ({
      ...text,
      marks: text.marks?.filter(mark => TEASER_MARKS.includes(mark.type)),
    }))

  // remove any tailing colons and white spaces
  if (texts.length > 0) {
    texts[texts.length - 1].text = (texts[texts.length - 1].text as string).replace(/[.|\s]*$/, '')
  }

  return {
    type: 'doc',
    content: [
      {
        type: 'paragraph',
        content: [
          ...texts,
          {
            type: 'text',
            text: '...',
          },
        ],
      },
    ],
  }
}
